import React, { useState, useRef, useEffect } from 'react';

import styled from '@emotion/styled';

import { colors } from 'src/styles/colors';

const PinInputContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 -8px;
`;

const PinInput = styled.input<{ width: string; border: string }>`
  ${(props) => props.width && `width: ${props.width}`};
  ${(props) => props.width && `height: ${props.width}`};
  border: ${(props) =>
    props.border === '0' ? '0' : `1px solid ${props.border}`};
  background: ${(props) =>
    props.border === '0' ? colors.AccentCodGrayFaqBgColor : 'transparent'};
  color: ${(props) => (props.border === '0' ? colors.white : colors.black)};
  font-size: 24px;
  margin: 0 8px;
  text-align: center;
  border-radius: 7px;
  -webkit-text-security: disc;
  -moz-webkit-text-security: disc;
  -moz-text-security: disc;
  text-security: disc;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border: ${(props) =>
      props.border === '0' ? '0' : `1px solid ${props.border}`};
  }
`;

type PinInputProps = {
  onComplete: (pin: string) => void;
  width?: string;
  border?: string;
};

const PinComponent: React.FC<PinInputProps> = ({
  onComplete,
  width = '50px',
  border = '0'
}) => {
  const [pin, setPin] = useState<string>('');
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Backspace') {
        event.preventDefault();
        const index = inputRefs.findIndex(
          (inputRef) => inputRef.current === document.activeElement
        );
        if (index > 0) {
          const newPin = pin.substring(0, index - 1) + pin.substring(index);
          onComplete(newPin?.slice(0, 4));
          setPin(newPin?.slice(0, 4));
          inputRefs[index - 1].current?.focus();
        } else if (index === 0) {
          onComplete('');
          setPin('');
          inputRefs[index].current?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [inputRefs, pin]);

  const handleInputChange = (index: number, value: string) => {
    const newPin = pin.substring(0, index) + value + pin.substring(index + 1);
    onComplete(newPin?.slice(0, 4));
    setPin(newPin?.slice(0, 4));
    if (index < inputRefs.length - 1 && value !== '') {
      inputRefs[index + 1].current?.focus();
    } else if (index === inputRefs.length - 1 && value.length > 1) {
      // limit the input value to only one digit for the last input field
      const lastDigit = value[value.length - 1];
      const newLastPin = pin.substring(0, index) + lastDigit;
      onComplete(newLastPin);
      setPin(newLastPin);
    }
  };

  return (
    <PinInputContainer>
      {inputRefs.map((inputRef, index) => (
        <PinInput
          key={index}
          ref={inputRef}
          type="number"
          inputMode="numeric"
          min={0}
          max={9}
          value={pin[index] || ''}
          onChange={(event) => handleInputChange(index, event.target.value)}
          width={width}
          border={border}
          data-testid={`pp-input-box-${index + 1}`}
        />
      ))}
    </PinInputContainer>
  );
};

export default PinComponent;
